import { createColumnHelper, flexRender, useTable } from "@tanstack/react-table"
import { Button } from "design-system/atoms/Button"
import DateDisplay from "design-system/molecules/DateDisplay"
import {
  Table,
  TableBody,
  TableCell,
  TableEmptySlate,
  TableHead,
  TableHeader,
  TableRow,
  TableSkeleton,
} from "design-system/molecules/Table"
import { features, type Features } from "lib/table"
import { useGetBackofficeReferralsQuery } from "modules/backoffice/hooks/referrals/useGetBackofficeReferralsQuery"
import { useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { BackofficeReferralsResponseDataSchema } from "schema"
import { z } from "zod"
import { BackofficeAddReferralCreditsModal } from "./BackofficeAddReferralCreditsModal"

const columnHelper = createColumnHelper<
  Features,
  z.infer<typeof BackofficeReferralsResponseDataSchema>[number]
>()

const getWebsiteHref = (website: string) =>
  website.startsWith("http://") || website.startsWith("https://") ? website : `https://${website}`

type ReferralsQueryReturn = ReturnType<typeof useGetBackofficeReferralsQuery>

type Props = {
  referrals: ReferralsQueryReturn["data"]
  isPending: ReferralsQueryReturn["isPending"]
}

export function BackofficeReferralsTable({ referrals, isPending }: Props) {
  const { t } = useTranslation()
  const [selectedReferral, setSelectedReferral] = useState<
    z.infer<typeof BackofficeReferralsResponseDataSchema>[number] | null
  >(null)

  const columns = useMemo(
    () =>
      columnHelper.columns([
        columnHelper.accessor("createdAt", {
          header: t("backoffice.referrals.table.createdAt"),
          cell: ({ getValue }) => <DateDisplay date={getValue()} />,
        }),
        columnHelper.accessor("companyName", {
          header: t("backoffice.referrals.table.companyName"),
        }),
        columnHelper.accessor("contactName", {
          header: t("backoffice.referrals.table.contactName"),
        }),
        columnHelper.accessor("role", {
          header: t("backoffice.referrals.table.role"),
          cell: ({ getValue }) => getValue() || "-",
        }),
        columnHelper.accessor("email", {
          header: t("backoffice.referrals.table.email"),
        }),
        columnHelper.accessor("phone", {
          header: t("backoffice.referrals.table.phone"),
        }),
        columnHelper.accessor("website", {
          header: t("backoffice.referrals.table.website"),
          cell: ({ getValue }) => {
            const website = getValue()

            if (!website) return "-"

            return (
              <a
                href={getWebsiteHref(website)}
                target="_blank"
                rel="noreferrer"
                className="text-primary hover:underline"
              >
                {website}
              </a>
            )
          },
        }),
        columnHelper.accessor("employee.name", {
          header: t("backoffice.referrals.table.createdBy"),
          cell: ({ row }) => row.original.employee.name,
        }),
        columnHelper.accessor("organization.name", {
          header: t("backoffice.referrals.table.organization"),
          cell: ({ row }) => row.original.organization.name,
        }),
        columnHelper.accessor("credits", {
          header: t("backoffice.referrals.table.credits"),
        }),
        columnHelper.display({
          id: "actions",
          header: t("backoffice.referrals.table.actions"),
          cell: ({ row }) => (
            <Button variant="outline" size="sm" onClick={() => setSelectedReferral(row.original)}>
              {t("backoffice.referrals.addCredits")}
            </Button>
          ),
        }),
      ]),
    [t],
  )

  const table = useTable({
    data: referrals ?? [],
    columns,
    features,
  })

  function renderContent() {
    if (isPending || referrals === undefined) {
      return <TableSkeleton colSpans={columns.map((column) => column.meta?.colSpan ?? 1)} />
    }

    if (referrals.length === 0) {
      return (
        <TableEmptySlate
          colSpan={columns.reduce((acc, column) => acc + (column.meta?.colSpan ?? 1), 0)}
          message={t("backoffice.referrals.table.empty")}
        />
      )
    }

    return table.getRowModel().rows.map((row) => (
      <TableRow key={row.id}>
        {row.getVisibleCells().map((cell) => (
          <TableCell key={cell.id} {...cell.column.columnDef.meta}>
            {flexRender(cell.column.columnDef.cell, cell.getContext())}
          </TableCell>
        ))}
      </TableRow>
    ))
  }

  return (
    <>
      <Table>
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <TableHead key={header.id} {...header.column.columnDef.meta}>
                  {flexRender(header.column.columnDef.header, header.getContext())}
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>{renderContent()}</TableBody>
      </Table>
      {selectedReferral && (
        <BackofficeAddReferralCreditsModal
          referralLeadId={selectedReferral.id}
          organizationId={selectedReferral.organization.id}
          onOpenChange={() => setSelectedReferral(null)}
        />
      )}
    </>
  )
}
